import { atom } from 'jotai';
import { atomWithReset, atomWithReducer } from 'jotai/utils';
import type { CompletedItemsUpdateAction, ExecutorVar, NetworkLog } from '~/common/vm/protocol';
import type { ClickMode } from '~/common/recorder/recorder-operation';
import type { LogType } from '~/common/vm/protocol';
import type { RecorderExecutionType } from '~/common/vm/shared-types';
import type { VmStatus } from '~/hooks/vm/useVm';
import type { RecorderInitiatorLocation } from '~/server/logic/recorder/recorder-types';
import { match } from 'ts-pattern';
import type { OperationParentContext } from '~/server/logic/steps/operation/types';

export type RecorderInitiator = {
  executionType: RecorderExecutionType;
  location?: RecorderInitiatorLocation;
};

// TODO: this should have the parentContext instead
// fix recorderCursor server-client cursor (recorded.recorderCursor, cursor-update.recorderCursor, execution-type-change.newRecordCursor)
// to make them pass right data back to backend
export type RecorderCursor = {
  // stepId is always required for:
  // - UI invalidation
  // - Context on where the component operation is in the step
  // - Recorder cursor positioning
  stepId: string;
  // parent context is optional and indicates that we're in a parent op (component or conditional)
  parentContext?: OperationParentContext;

  stepOrder: number;
  operation?: {
    id: string;
    order: number;
  };
};

export const recorderInitiatorAtom = atom<RecorderInitiator | undefined>(undefined);
export const recorderCursorAtom = atom<RecorderCursor | undefined>(undefined);

// Combines the atoms to make state easier to work with
export const recorderStateAtom = atom((get) => {
  const recorderInitiator = get(recorderInitiatorAtom);
  const recorderCursor = get(recorderCursorAtom);
  return recorderInitiator ?
      {
        executionType: recorderInitiator.executionType,
        parentContext: recorderCursor?.parentContext,
        stepId: recorderCursor?.stepId ?? recorderInitiator?.location?.stepId,
        stepOrder: recorderCursor?.stepOrder ?? 0,
        operation: recorderCursor?.operation,
      }
    : undefined;
});

export const recorderPausedAtom = atom<boolean>(false);
export const recorderConsoleLogsAtom = atomWithReset<{ msg: string; logType: LogType; addedAtEpochMs: number }[]>([]);
export const recorderNetworkLogsAtom = atomWithReset<NetworkLog[]>([]);

export const vmStatusAtom = atom<VmStatus | undefined>(undefined);

export const recordTypeAtom = atom<string>('action');
export const autoHealStepAtom = atomWithReset<string | undefined>(undefined);
// Timeout overrides for action/ assertion operations
export const timeoutAtom = atomWithReset<{ stepId: string; totalTimeoutMs: number } | undefined>(undefined);
export const fastAutoHealAtom = atomWithReset<{ stepId: string; operationId: string }[]>([]);
export const contextMenuDropDownAtom = atom<boolean>(false);
export const recorderClickModeAtom = atom<ClickMode>('center');
export const wdioSocketAtom = atom<WebSocket | undefined>(undefined);
export const recorderTabsOpenAtom = atom<boolean>(false);
export const executedVariablesAtom = atom<ExecutorVar[]>([]);

// stores whether conditional blocks have been resolved to true or false (keyed by conditional block id)
export const conditionalBlockResultsAtom = atom<Record<string, boolean>>();

export type CompletedState = {
  completedOperationIds: string[];
  completedStepIds: string[];
};

const initialCompletedState: CompletedState = {
  completedOperationIds: [],
  completedStepIds: [],
};

const completedItemsReducer = (state: CompletedState, action: CompletedItemsUpdateAction): CompletedState =>
  match(action)
    .with({ type: 'append-operation' }, ({ operationId }) => {
      return {
        ...state,
        completedOperationIds: [...state.completedOperationIds, operationId],
      };
    })
    .with({ type: 'append-step' }, ({ stepId }) => {
      return {
        ...state,
        completedStepIds: [...state.completedStepIds, stepId],
      };
    })
    .with({ type: 'reset' }, () => {
      return initialCompletedState;
    })
    .exhaustive();

export const completedItemsAtom = atomWithReducer(initialCompletedState, completedItemsReducer);

// Tracks newly created dummy steps that should be cleaned up if still empty when VM is closed
export const createdDummyStepsAtom = atomWithReset<string[]>([]);
